/**
 * @file LumiLogo.tsx
 * @description Reusable Lumi logo SVG component
 *
 * The logo uses the custom "LUMI" wordmark with accent color styling.
 * Can be used as a button (with onClick) or static element.
 */

import type { ReactElement } from "react";
interface LumiLogoProps {
  /** Optional click handler - if provided, logo becomes a button */
  readonly onClick?: () => void;
  /** CSS class names for custom sizing/styling */
  readonly className?: string;
  /** Accessible label for the logo */
  readonly ariaLabel?: string;
}

/**
 * Lumi brand logo component
 *
 * @example
 * ```tsx
 * // As a button
 * <LumiLogo
 *   onClick={() => navigate('/home')}
 *   className="h-6"
 *   ariaLabel="Go to home"
 * />
 *
 * // As static logo
 * <LumiLogo className="h-8" ariaLabel="Lumi" />
 * ```
 */
export function LumiLogo({ onClick, className = "h-4 sm:h-6", ariaLabel = "Lumi" }: LumiLogoProps): ReactElement {
  const svgElement = (
    <svg
      className={`w-auto ${className}`}
      viewBox="0 0 504.77 190"
      xmlns="http://www.w3.org/2000/svg"
      fill="currentColor"
      aria-hidden="true"
    >
      <path
        className="fill-lumi-accent"
        d="M295.13.85l70.84,112.93,3.17-3.33L437.6.85h13.43v185.23h-13.84V29.29l-70.03,110.48-71.63-110.48v156.79h-13.03V.85h12.62ZM111.54.85v125.51c5.87,79.03,112.3,85.36,129.56,13.93.53-2.21,2.33-9.84,2.33-11.48V.85h-13.84v123.88c-3.27,35.61-23.71,53.72-59.83,50.77-24.8-2.02-44.38-24.28-44.38-49.14V.85h-13.84ZM0,.85v185.23h102.58v-11.78l-1.22-1.22H13.84V.85H0ZM490.93,0v186.08h13.84V0h-13.84Z"
      />
    </svg>
  );

  if (onClick) {
    return (
      <button onClick={onClick} className="hover:opacity-80 transition-opacity" aria-label={ariaLabel}>
        {svgElement}
      </button>
    );
  }

  return <div aria-label={ariaLabel}>{svgElement}</div>;
}
